import { OrganizationAPI } from "@/API/Organization"
import { appActions } from "@/redux/slice/appSlice"
import { AppDispatch } from "@/redux"
import { organizationActions } from "@/redux/slice/organizationSlice"
import { OrganizationCreateUpdateBody } from "@/types/organizations"
import { showNotification } from "./notificationService"
import { OrganizationMessage } from "@/constants/organization/organization"
import { getAllInvitedUsersInOrganization, getAllUsersInOrganization, getUser } from "./userService"
import { getAllWorkflows } from "./workflowService"
import { getAllApplications } from "./applicationsService"
import { billingActions } from "@/redux/slice/billingSlice"
import { getInvoices, getOneTimeFees, getSubscription } from "./billingService"
import { getCredentials } from "./credentialsService"
import { getAllTransformationTemplates } from "./transformationService"

export const getAllOrganizations = (
  token: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await OrganizationAPI.getAll(token)
  if (resp.data?.organizations) {
    dispatch(organizationActions.setOrganizations(resp.data.organizations))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const createOrganization = (
  token: string, body: OrganizationCreateUpdateBody
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await OrganizationAPI.create(token, body)
  showNotification(
    resp,
    OrganizationMessage.ORGANIZATION_CREATE_SUCCESSFUL,
    {}
  )
  if (resp.data) {
    await dispatch(getUser(token))
    await dispatch(getAllOrganizations(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const updateOrganization = (
  token: string, body: OrganizationCreateUpdateBody, orgId: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await OrganizationAPI.update(token, body, orgId)
  showNotification(
    resp,
    OrganizationMessage.ORGANIZATION_UPDATE_SUCCESSFUL,
    {}
  )
  if (resp.data) {
    await dispatch(getAllOrganizations(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const deleteOrganization = (
  token: string, orgId: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await OrganizationAPI.delete(token, orgId)
  showNotification(
    resp,
    OrganizationMessage.ORGANIZATION_DELETE_SUCCESSFUL,
    {}
  )
  if (resp.data) {
    await dispatch(getAllOrganizations(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const switchOrganization = (
  token: string, orgId: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await OrganizationAPI.switchOrganization(token, { organizationId: orgId })
  showNotification(
    resp,
    OrganizationMessage.LOGGED_IN_AS_ORGANIZATION,
    {}
  )
  if (resp.data) {
    dispatch(billingActions.clearBilling())
    const userResp = await dispatch(getUser(token))
    await dispatch(getAllOrganizations(token))
    await dispatch(getAllUsersInOrganization(token))
    await dispatch(getAllInvitedUsersInOrganization(token))
    await dispatch(getAllApplications(token))
    await dispatch(getAllWorkflows(token))
    await dispatch(getCredentials(token))
    await dispatch(getAllTransformationTemplates(token))
    // not for 'user' role
    if (userResp?.user?.role !== 'user') {
      const orgId = userResp?.user?.currentOrganization?.id as string
      dispatch(getSubscription(token, orgId))
      dispatch(getInvoices(token, orgId))
      dispatch(getOneTimeFees(token, orgId))
    }
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const getOrgAPIKey = (
  token: string,
  orgId: string,
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await OrganizationAPI.getOrgAPIKey(token, orgId)
  if (resp.data) {
    if (resp.data.length > 0) {
      dispatch(appActions.setLoading(false))
      return resp
    } else {
      const createAPIKeyResp = await OrganizationAPI.createOrgAPIKey(token, orgId)
      if (createAPIKeyResp.data) {
        dispatch(appActions.setLoading(false))
        return { data: [createAPIKeyResp.data], err: createAPIKeyResp.err }
      }
    }
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const createOrgAPIKey = (
  token: string,
  orgId: string,
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await OrganizationAPI.createOrgAPIKey(token, orgId)
  dispatch(appActions.setLoading(false))
  return resp
}

export const removeOrgAPIKey = (
  token: string,
  orgId: string,
  apiKeyId: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await OrganizationAPI.removeOrgAPIKey(token, orgId, apiKeyId)
  dispatch(appActions.setLoading(false))
  return resp
}
